import { useState, useEffect } from 'react';
import { Bell, BellOff, X, Loader2, CheckCircle2, TestTube } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import usePushNotifications, { DISMISS_KEY } from '../../hooks/usePushNotifications';

const DELAY_MS = 10000; // wait 10s after login before showing banner
const DISMISS_DAYS = 14;

export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const { supported, permission, subscribed, loading, subscribe } = usePushNotifications();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user || !supported || subscribed || permission === 'denied') return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const days = (Date.now() - Number(dismissed)) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_DAYS) return;
    }
    const timer = setTimeout(() => setShow(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [user, supported, subscribed, permission]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const handleSubscribe = async () => {
    const r = await subscribe();
    if (r.ok) {
      toast.success('Notifiche attivate! Ti avviseremo per nuove emergenze e messaggi.');
      setShow(false);
    } else {
      toast.error(r.error || 'Errore attivazione notifiche');
    }
  };

  if (!show || !user || subscribed || !supported) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-40 bg-white border-2 border-[#0A84FF] shadow-2xl"
      data-testid="push-prompt"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-12 h-12 flex-shrink-0 bg-[#0A84FF] flex items-center justify-center">
          <Bell size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">
            Attiva le notifiche
          </div>
          <p className="text-[11px] text-[#4A6B8A] mt-1 leading-snug">
            Ricevi avvisi su nuove emergenze, messaggi della chat e aggiornamenti del tuo account — anche con l'app chiusa.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="flex items-center gap-1 px-3 py-2 bg-[#0A84FF] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#0070E0] disabled:opacity-40"
              data-testid="push-enable-btn"
            >
              {loading ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
              Attiva
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#4A6B8A] hover:text-[#0A84FF]"
              data-testid="push-dismiss-btn"
            >
              Più tardi
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 text-[#6B8AB5] hover:text-[#0A84FF] hover:bg-[#0A84FF]/10"
          aria-label="Chiudi"
          data-testid="push-close-btn"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// Settings panel widget — for profile page
export function PushNotificationSettings() {
  const { supported, permission, subscribed, loading, subscribe, unsubscribe, test } = usePushNotifications();
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    const r = await test();
    setTesting(false);
    if (r.ok) toast.success('Notifica di test inviata. Controlla il dispositivo.');
    else toast.error(r.error || 'Errore');
  };

  return (
    <div className="border border-[#B8D4E8] bg-white p-4" data-testid="push-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center ${subscribed ? 'bg-[#0A84FF]/10 text-[#0A84FF]' : 'bg-[#E8F4FD] text-[#4A6B8A]'}`}>
            {subscribed ? <Bell size={18} /> : <BellOff size={18} />}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Notifiche Push</div>
            <div className="text-[11px] text-[#4A6B8A]">
              {!supported && 'Non supportate da questo browser'}
              {supported && permission === 'denied' && '⚠️ Permesso negato — abilita dalle impostazioni del browser'}
              {supported && permission !== 'denied' && (subscribed ? 'Attive · ricevi notifiche in tempo reale' : 'Disattivate')}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {subscribed && (
            <button
              onClick={handleTest}
              disabled={testing}
              className="flex items-center gap-1 px-3 py-2 border border-[#0A84FF] text-[#0A84FF] text-xs font-bold uppercase tracking-widest hover:bg-[#0A84FF]/10 disabled:opacity-40"
              data-testid="push-test-btn"
            >
              {testing ? <Loader2 size={12} className="animate-spin" /> : <TestTube size={12} />}
              Test
            </button>
          )}
          {!subscribed ? (
            <button
              onClick={subscribe}
              disabled={loading || !supported || permission === 'denied'}
              className="flex items-center gap-2 px-4 py-2 bg-[#0A84FF] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#0070E0] disabled:opacity-40"
              data-testid="push-subscribe-btn"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
              Attiva Notifiche
            </button>
          ) : (
            <button
              onClick={unsubscribe}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-[#FF3B30] text-[#FF3B30] text-xs font-bold uppercase tracking-widest hover:bg-[#FF3B30]/10 disabled:opacity-40"
              data-testid="push-unsubscribe-btn"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <BellOff size={12} />}
              Disattiva
            </button>
          )}
        </div>
      </div>
      {subscribed && (
        <div className="mt-3 flex items-center gap-1 text-[10px] text-[#32D74B] uppercase tracking-widest">
          <CheckCircle2 size={10} /> Le notifiche push sono attive su questo dispositivo
        </div>
      )}
    </div>
  );
}
